// Mock data — paridade com V7 (linhas exatas da tabela #table-obras).
// Quando V7 e V8 forem comparados lado a lado, este é o single source of truth.
// V8 só ACRESCENTA a este array; não diverge.
//
// F6.6: exports mutáveis (export let) para permitir hidratação via ETL snapshot.
// heroSpark permanece const (métrica derivada de receitaMensal — não está no ETL).

// ─── ETL hydration (F6.6) ────────────────────────────────────────────────────
// Called by snapshot.js when ETL data is available at runtime.
// Overwrites mutable exports with real data; heroSpark stays derived.

import { normalizeObra } from './etl-normalize.js';

/**
 * Obra base mock (lista de obras). Mantém a estrutura esperada pela view.
 * @type {Array<Object>}
 */
export let obras = [
  { nome: 'Torre A', tipo: 'Edifício', status: 'Em progresso', avanco: 68, orcado: 12000000, executado: 8500000, gap: -3.5, atrasoDias: 2 },
  { nome: 'Torre B', tipo: 'Loteamento', status: 'Atenção', avanco: 43, orcado: 9800000, executado: 4120000, gap: -7.4, atrasoDias: 1 },
  { nome: 'Torre C', tipo: 'Comercial', status: 'Pendente', avanco: 31, orcado: 6700000, executado: 3100000, gap: -2.9, atrasoDias: 0 },
  { nome: 'Torre D', tipo: 'Infraestrutura', status: 'Concluída', avanco: 55, orcado: 15200000, executado: 8600000, gap: 2.1, atrasoDias: 0 },
  { nome: 'Residencial Parque', tipo: 'Loteamento', status: 'Em progresso', avanco: 72, orcado: 8400000, executado: 6048000, gap: 1.2, atrasoDias: 0 },
  { nome: 'Jardim Europa', tipo: 'Loteamento', status: 'Atenção', avanco: 38, orcado: 5600000, executado: 2128000, gap: -5.1, atrasoDias: 3 },
  { nome: 'Horizon Hills', tipo: 'Loteamento', status: 'Em progresso', avanco: 64, orcado: 7200000, executado: 4608000, gap: 0.8, atrasoDias: 0 },
  { nome: 'Ed. Central', tipo: 'Comercial', status: 'Planejado', avanco: 0, orcado: 4500000, executado: 0, gap: 0, atrasoDias: 0 },
  // Obras 9-14: expansão para paridade V7 (V7 tabela tem 14 rows)
  { nome: 'Torre E', tipo: 'Edifício', status: 'Em progresso', avanco: 82, orcado: 14500000, executado: 11890000, gap: 1.5, atrasoDias: 0 },
  { nome: 'Torre F', tipo: 'Edifício', status: 'Em progresso', avanco: 57, orcado: 11300000, executado: 6441000, gap: -4.2, atrasoDias: 2 },
  { nome: 'Loteamento Bosque', tipo: 'Loteamento', status: 'Atenção', avanco: 29, orcado: 6200000, executado: 1798000, gap: -8.1, atrasoDias: 5 },
  { nome: 'Galpão Logístico', tipo: 'Comercial', status: 'Em progresso', avanco: 91, orcado: 8900000, executado: 8100000, gap: 3.7, atrasoDias: 0 },
  { nome: 'Ponte Viária', tipo: 'Infraestrutura', status: 'Atenção', avanco: 46, orcado: 18700000, executado: 8602000, gap: -6.3, atrasoDias: 4 },
  { nome: 'Estação de Tratamento', tipo: 'Infraestrutura', status: 'Em progresso', avanco: 73, orcado: 5100000, executado: 3723000, gap: 0.4, atrasoDias: 0 }
];

// ─── Dados de série temporal (overview) ──────────────────────────────────────
// V7 hardcoda estes arrays no overview. Movidos para mock.js (Fase 4) para
// serem single source of truth. Em V8 puro, viriam de API; aqui documentamos.

/** Array de meses para a série temporal (12 meses). */
export let meses12 = ['Abr/25','Mai/25','Jun/25','Jul/25','Ago/25','Set/25','Out/25','Nov/25','Dez/25','Jan/26','Fev/26','Mar/26'];

/** Montante mensal de receita (mock). */
export let receitaMensal = [6200000,7100000,7500000,8200000,8600000,9000000,9200000,8800000,9500000,9800000,9400000,8734281];

/** Distribuição por tipo de obra (mock). */
export let composicaoTipo = [
  { name: 'Edifícios', value: 58 },
  { name: 'Loteamentos', value: 27 },
  { name: 'Comercial', value: 15 }
];

/** Série de margens de lucro por mês (mock). */
export let margemSpark = [38, 40, 39, 42, 41, 43, 42, 40, 44, 43, 41, 40];

/** Sparkline de hero derived from receitaMensal (mock). */
export let heroSpark = receitaMensal.map((v) => Math.round(v / 1000));

/** Meta anual em percentuais (mock). */
export let metaAnualPercent = 72;

/**
 * Aplica um snapshot de dados de origem para hydratar o estado mock (em ambiente real, ETL substituiria).
 * @param {Object} s
 */
export function _hydrateFromSnapshot(s) {
  obras = s.obras.map(normalizeObra);
  meses12 = s.meses12;
  receitaMensal = s.receitaMensal;
  composicaoTipo = s.composicaoTipo;
  margemSpark = s.margemSpark;
  metaAnualPercent = s.metaAnualPercent;
  // Recompute heroSpark from updated receitaMensal (derived metric, not in ETL)
  heroSpark = receitaMensal.map((v) => Math.round(v / 1000));
}

// ─── P0: Delta refresh (in-place mutation) ─────────────────────────────────
// Applies a snapshot delta without full reassign of the obras array.
// In-place mutation: splice/Object.assign preserve references.
// Required for ESM live-binding to propagate to consumers.
//
// @param {Object} delta — output of snapshot-delta.diff()

/**
 * Aplica um delta de snapshot para atualizar dados mock in-place.
 * @param {Object} delta
 */
export function _applyDelta(delta) {
  // 1. Remove (silent skip if not found)
  for (const r of delta.removed) {
    const idx = obras.findIndex(o => o.nome === r.nome);
    if (idx >= 0) obras.splice(idx, 1);
  }

  // 2. Modify (in-place merge — preserves object ref)
  for (const m of delta.modified) {
    const target = obras.find(o => o.nome === m.nome);
    if (target) Object.assign(target, m);
  }

  // 3. Add
  for (const a of delta.added) {
    obras.push(a);
  }

  // 4. Series (if changed)
  if (delta.seriesChanged && delta.next?.series) {
    const s = delta.next.series;
    if (s.meses12) {
      meses12.length = 0;
      meses12.push(...s.meses12);
    }
    if (s.receitaMensal) {
      receitaMensal.length = 0;
      receitaMensal.push(...s.receitaMensal);
    }
    if (s.composicaoTipo) {
      composicaoTipo.length = 0;
      composicaoTipo.push(...s.composicaoTipo);
    }
    if (s.margemSpark) {
      margemSpark.length = 0;
      margemSpark.push(...s.margemSpark);
    }
    if (s.metaAnualPercent !== undefined) metaAnualPercent = s.metaAnualPercent;
    // Recompute heroSpark (derived)
    heroSpark.length = 0;
    heroSpark.push(...receitaMensal.map((v) => Math.round(v / 1000)));
  }
}
